import React, { useEffect } from 'react';
import { Alert, Box, Col, Heading, Row, themes } from '@digitools/honest-react';
import { getEnvVariable } from 'src/utils/envUtils';
import LoginBox from './LoginBox';
import { useLocation } from 'react-router-dom';
import { VanityPageText } from '../../vanityPage/constants/VanityPageText';
import useTranslate from 'src/hooks/useTranslate';
import { StyledPageContainer, StyledSpoeButton, } from '../../vanityPage/components/StyledComponents';
import { TEALIUM_EVENT, TEALIUM_PAGE_VIEW } from 'src/constants/tealiumConstants';
import useTealium from 'src/hooks/useTealium';
import EmployeeGraphic from 'src/assets/employee-graphic.svg';
import EmployerGraphic from 'src/assets/employer-graphic.svg';
import useMediaQuery from 'src/hooks/useMediaQuery';
import { MOBILE_SPOE } from 'src/constants/ScreenSizeConstants';
import Notifications, { FunctionalArea } from 'src/components/Notifications/Notifications';
import styled from 'styled-components';
import { BackgroundWave } from '@digitools/honest-ui';
// Workaround for Login's current, non-honest-ui markup — needed after replacing Container with Grid at app-level.
// Revisit alongside App.tsx's h-100 when Login is rebranded: CX-3948.
// position:relative gives wave something to anchor to;
// height:100% resolves against the app-level Grid's h-100 (App.tsx) so this box
// stretches to fill the gap .main's flex-grow leaves on this short page, letting the wave fill
// it instead of leaving blank whitespace above the footer.
const LoginWrapper = styled.div `
    height: 100%;
    position: relative;
`;
const EnableSpoeLogin = ({ isMobile, handleGoToLincolnClick, spoeLoginTopOffset, goToLincoln, employeesOrClaimants, employeeDetails, pleaseSaveUrl, }) => {
    return (<LoginWrapper>
      <Notifications functionalArea={FunctionalArea.LOGIN}/>
      <StyledPageContainer className={isMobile ? 'pt-3 pl-0 pr-0 mt-0' : 'mt-0 pl-0 pr-0'}>
        <Row className={`w-100 mx-0 ${isMobile ? 'px-0' : 'px-5'}`}>
          <Col lg={8} className={isMobile ? 'w-100 px-0 mx-0' : 'px-2'} style={{ borderBottom: `${isMobile ? `2px solid ${themes.rebrand.palette.grayLight}` : 'transparent'}` }}>
            <Box className={`pl-0 pr-0 border-0 bg-transparent`}>
              <Row className={isMobile ? 'd-block pb-3' : 'd-flex'}>
                <Col style={{ paddingTop: `${isMobile ? '0' : '5.5rem'}`, gap: '2rem' }} className={`${isMobile
            ? 'text-center mb-2 px-0 d-flex flex-column align-items-center'
            : 'px-2 align-items-start d-flex'}`}>
                  <EmployeeGraphic />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Heading elemType={'h1'} elemStyle={'h2'} color={'primaryDarker'} className={'m-0'} style={{
            lineHeight: '36px',
            fontSize: '30px',
            fontFamily: 'Merriweather',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
        }}>
                      {employeesOrClaimants}
                    </Heading>
                    <p className={isMobile ? 'm-0 pt-3 pb-0' : 'p-0 m-0'}>
                      {employeeDetails}
                      <span>
                        <a style={{ color: '#036DCB' }} className={'font-weight-bold'} href={`${getEnvVariable('lfgHomeUrl')}/secure/login`}>
                          www.LincolnFinancial.com.
                        </a>
                      </span>
                    </p>
                    <p className={`pt-0 m-0 pb-4 ${isMobile ? 'mb-2' : 'mb-4'}`}>{pleaseSaveUrl}</p>
                    <StyledSpoeButton as={'a'} onClick={handleGoToLincolnClick} {...{ href: `${getEnvVariable('lfgHomeUrl')}/secure/login` }}>
                      {goToLincoln}
                    </StyledSpoeButton>
                  </div>
                </Col>
              </Row>
            </Box>
          </Col>
          <Col lg={4} className={`${isMobile ? 'pt-2 px-0 pb-2' : 'px-2'} overflow-visible`}>
            <div className={`position-relative ${isMobile ? 'd-flex justify-content-center' : ''}`} style={{
            paddingTop: spoeLoginTopOffset,
            paddingBottom: 0,
            overflow: 'visible',
        }}>
              <div className={`${isMobile ? 'mt-4' : ''} position-absolute w-100 justify-content-center d-flex`} style={{
            top: spoeLoginTopOffset,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            pointerEvents: 'none',
        }}>
                <EmployerGraphic />
              </div>
              <LoginBox />
            </div>
          </Col>
        </Row>
      </StyledPageContainer>
      <BackgroundWave />
    </LoginWrapper>);
};
const Login = () => {
    const { t } = useTranslate();
    const { trackView, trackEvent } = useTealium();
    const { search } = useLocation();
    const urlParams = new URLSearchParams(search);
    const hasError = urlParams.get('error') === 'true';
    const spoeLoginTopOffset = '2.5rem';
    const isMobile = useMediaQuery(`(max-width: ${MOBILE_SPOE})`);
    const handleGoToLincolnClick = () => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
            event_type: TEALIUM_EVENT.EVENT_TYPE.LOGIN,
            event_name: TEALIUM_EVENT.EVENT_NAME.GO_TO_LINCOLN,
            event_version: TEALIUM_EVENT.EVENT_VERSION.ACCOUNT_LOOKUP,
        });
    };
    useEffect(() => {
        trackView({
            page_l3: TEALIUM_PAGE_VIEW.PAGE_L3.LOGIN,
            page_l4: TEALIUM_PAGE_VIEW.PAGE_L4.HOME_PAGE.HOME_PAGE_ER,
        });
    }, []);
    return (<>
      {hasError && (<Alert data-public={true} type={'warning'} closeable={true} data-testid={'login-error-banner'}>
          {t(VanityPageText.errorBannerMessage)}
          <a href={getEnvVariable('lfgHomeUrl')} style={{ fontWeight: 'bold' }}>
            {t(VanityPageText.errorBannerLinkText)}
          </a>
        </Alert>)}
      <EnableSpoeLogin employeeDetails={t(VanityPageText.employeeDetails)} employeesOrClaimants={t(VanityPageText.employeesOrClaimants)} spoeLoginTopOffset={spoeLoginTopOffset} goToLincoln={t(VanityPageText.goToLincoln)} handleGoToLincolnClick={handleGoToLincolnClick} pleaseSaveUrl={t(VanityPageText.pleaseSaveUrl)} isMobile={isMobile}/>
    </>);
};
export default Login;
